<template>
  <div class="w-full pb-20">
    <div class="space-y-4">
      <!-- Header -->
      <div class="md:flex items-center justify-between mb-8">
        <div>
          <h1 class="text-[32px] font-semibold text-foreground mb-2">
            Credits
          </h1>
          <p class="text-[15px] text-muted-foreground">
            Manage and top-up your credits here.
          </p>
        </div>
        <button
          @click="showTopUpDialog = true"
          class="flex items-center mt-2 md:mt-0 gap-2 px-6 py-3 bg-foreground dark:bg-foreground/10 text-background dark:text-foreground rounded-full hover:opacity-90 transition-opacity font-medium text-[14px]"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 3.33301V12.6663"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M12.6667 8H3.33337"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          Top up credits
        </button>
      </div>

      <!-- Main Content -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left Column - Credits Info & Usage -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Credits Balance Card -->
          <div class="bg-card border border-border rounded-[12px] p-4 md:p-6">
            <div class="flex items-start gap-3 mb-6">
              <div
                class="w-10 h-10 bg-foreground/10 rounded-lg flex items-center justify-center"
              >
                <RefreshCw :size="20" class="text-foreground" />
              </div>
              <div>
                <p class="text-[13px] text-muted-foreground mb-1">
                  Total credits remaining
                </p>
                <p class="text-[32px] font-semibold text-foreground">
                  {{ formatCredits(totalCredits) }}
                </p>
              </div>
            </div>

            <!-- Credits Usage Progress -->
            <div class="space-y-3">
              <div class="flex items-center justify-between text-[13px]">
                <span class="font-medium text-foreground">Credits usage</span>
                <span class="text-muted-foreground"
                  >0 of {{ totalCredits }} credits used</span
                >
              </div>
              <div class="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  class="h-full bg-foreground rounded-full transition-all duration-300"
                  :style="{ width: `${usagePercentage}%` }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Transaction History -->
          <div class="bg-card border border-border rounded-[12px] p-4 md:p-6">
            <h2 class="text-[18px] font-semibold text-foreground mb-6">
              Credits Usage History
            </h2>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-12">
              <Loader />
            </div>

            <!-- Empty State -->
            <div
              v-else-if="!loading && creditsUsage.length === 0"
              class="text-center py-12"
            >
              <div
                class="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 11L12 14L22 4"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <p class="text-muted-foreground text-[14px]">No usage yet</p>
            </div>

            <!-- Credits Usage Table -->
            <div v-else class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border">
                    <th
                      class="text-left py-3 px-4 text-[13px] font-medium text-muted-foreground"
                    >
                      Operation type
                    </th>
                    <th
                      class="text-left py-3 px-4 text-[13px] font-medium text-muted-foreground"
                    >
                      Credits
                    </th>
                    <th
                      class="text-left py-3 px-4 text-[13px] font-medium text-muted-foreground"
                    >
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="usage in paginatedCreditsUsage"
                    :key="usage.id"
                    class="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td class="py-4 px-4 text-[14px] text-foreground">
                      {{ usage.operation_type }}
                    </td>
                    <td class="py-4 px-4 text-[14px] text-foreground">
                      {{ usage.credits_used }}
                    </td>
                    <td class="py-4 px-4 text-[14px] text-muted-foreground">
                      {{ formatDate(usage.created_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div
                v-if="totalPages > 1"
                class="flex items-center justify-between mt-6 pt-4 border-t border-border"
              >
                <p class="text-[13px] text-muted-foreground">
                  Showing {{ startIndex + 1 }}-{{ endIndex }} of
                  {{ creditsUsage.length }} credits usage
                </p>
                <div class="flex items-center gap-2">
                  <button
                    @click="previousPage"
                    :disabled="currentPage === 1"
                    class="px-3 py-1.5 text-[13px] border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    @click="nextPage"
                    :disabled="currentPage === totalPages"
                    class="px-3 py-1.5 text-[13px] border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Info Cards -->
        <div class="space-y-6">
          <!-- How Credits Work -->
          <div class="bg-card border border-border rounded-[12px] p-4 md:p-6">
            <h3 class="text-[16px] font-semibold text-foreground mb-4">
              How credits work
            </h3>
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <p class="text-[13px] text-muted-foreground leading-relaxed">
                  Credits are used for AI-powered features like learning goal
                  generation and assessments
                </p>
              </div>
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <p class="text-[13px] text-muted-foreground leading-relaxed">
                  Each action consumes a specific amount of credits based on
                  complexity
                </p>
              </div>
              <div class="flex items-start gap-3">
                <div
                  class="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <p class="text-[13px] text-muted-foreground leading-relaxed">
                  Credits renew monthly based on your subscription plan
                </p>
              </div>
            </div>
          </div>

          <!-- Credit Costs -->
          <div class="bg-card border border-border rounded-[12px] p-4 md:p-6">
            <h3 class="text-[16px] font-semibold text-foreground mb-4">
              Credit costs
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <span class="text-[13px] text-foreground"
                  >Learning goal creation</span
                >
                <span class="text-[13px] font-medium text-foreground"
                  >10 credits</span
                >
              </div>
              <div
                class="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <span class="text-[13px] text-foreground">Assessment</span>
                <span class="text-[13px] font-medium text-foreground"
                  >10 credits</span
                >
              </div>
              <div
                class="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <span class="text-[13px] text-foreground"
                  >Certificate Purchase</span
                >
                <span class="text-[13px] font-medium text-foreground"
                  >10 credits</span
                >
              </div>
            </div>
          </div>

          <!-- Need More Credits -->
          <div
            class="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-[12px] p-6"
          >
            <h3 class="text-[16px] font-semibold text-foreground mb-2">
              Need more credits?
            </h3>
            <p class="text-[13px] text-muted-foreground mb-4">
              Upgrade your plan to get more monthly credits and unlock premium
              features.
            </p>
            <button
              @click="navigateToPricing"
              class="w-full px-4 py-2.5 bg-foreground dark:bg-foreground/10 dark:text-foreground text-background rounded-lg hover:opacity-90 transition-opacity font-medium text-[13px]"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    </div>

    <pre>{{ subscriptionData }}</pre>
    <!-- Top Up Credits Modal -->
    <div
      v-if="showTopUpDialog"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
    >
      <div
        class="relative bg-card w-full max-w-[600px] m-auto flex-col flex rounded-2xl shadow-2xl border border-border overflow-hidden animate-in fade-in zoom-in duration-200"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-border"
        >
          <h2 class="text-[24px] md:text-[28px] font-semibold text-foreground">
            Credits Top Up
          </h2>
          <button
            @click="showTopUpDialog = false"
            class="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-muted"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="px-4 md:px-6 py-6 space-y-6">
          <!-- Price Display -->
          <div class="text-center py-4">
            <div
              v-if="!isCustom || !calculating"
              class="animate-in fade-in duration-300"
            >
              <p
                class="text-[24px] md:text-[24px] font-bold text-foreground mb-1"
              >
                ₦{{ displayAmount }}
              </p>
              <div v-if="isCustom" class="flex flex-col items-center gap-2">
                <div class="relative w-full max-w-[200px]">
                  <input
                    v-model.number="customCredits"
                    type="number"
                    min="1"
                    ref="customInput"
                    class="w-full text-center text-[18px] font-medium bg-transparent border-b-2 border-foreground/20 focus:border-foreground outline-none py-1 transition-all"
                    placeholder="Enter credits"
                  />
                  <p class="text-[12px] text-muted-foreground mt-1">credits</p>
                  <p
                    v-if="isCustom && customCredits > 0 && customCredits < 10"
                    class="text-[12px] text-red-500 mt-1 font-medium animate-in fade-in duration-200"
                  >
                    Minimum 10 credits required
                  </p>
                </div>
              </div>
              <p v-else class="text-[16px] text-muted-foreground">
                {{ displayCredits }} credits
              </p>
            </div>
            <div v-else class="flex flex-col items-center justify-center py-6">
              <div
                class="w-8 h-8 border-3 border-foreground/30 border-t-foreground rounded-full animate-spin mb-3"
              ></div>
              <p class="text-[14px] text-muted-foreground animate-pulse">
                Calculating price...
              </p>
            </div>
          </div>

          <!-- Package Savings / Custom Price Badges -->
          <div class="space-y-3">
            <div
              v-if="!isCustom && selectedPackage && selectedPackage.savings > 0"
              class="px-4 py-2.5 bg-green-500/10 border border-green-500/20 rounded-xl animate-in fade-in zoom-in duration-300"
            >
              <div
                class="flex items-center justify-center gap-2 text-green-700"
              >
                <p class="text-[13px] font-semibold">
                  ✨ You're saving ₦{{
                    selectedPackage.savings.toLocaleString()
                  }}
                </p>
              </div>
            </div>

            <div
              v-if="isCustom && calculationResult"
              class="px-4 py-2.5 bg-foreground/5 border border-border rounded-xl animate-in fade-in zoom-in duration-300"
            >
              <div class="flex items-center justify-between text-[13px]">
                <span class="text-muted-foreground">Price per credit</span>
                <span class="font-semibold text-foreground"
                  >₦{{ calculationResult.price_per_credit }}</span
                >
              </div>
            </div>
          </div>

          <!-- Amount Selection -->
          <div class="relative min-h-[100px]">
            <div
              v-if="loadingPackages"
              class="flex flex-col items-center justify-center py-10"
            >
              <Loader />
              <p class="text-[13px] text-muted-foreground mt-4 animate-pulse">
                Loading packages...
              </p>
            </div>
            <div v-else>
              <h3 class="text-[16px] font-medium text-foreground mb-4">
                Select package
              </h3>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                  v-for="pkg in creditPackages"
                  :key="pkg.credits"
                  @click="selectPackage(pkg)"
                  :class="[
                    'px-4 py-4 flex flex-col items-center justify-center rounded-xl border-2 transition-all relative',
                    selectedPackage?.credits === pkg.credits && !isCustom
                      ? 'border-foreground bg-foreground/5 shadow-sm'
                      : 'border-border hover:border-foreground/50',
                  ]"
                >
                  <div
                    v-if="pkg.discount_percentage > 0"
                    class="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm"
                  >
                    -{{ pkg.discount_percentage }}%
                  </div>
                  <span class="text-[16px] font-semibold text-foreground"
                    >{{ formatCredits(pkg.credits) }} Credits</span
                  >
                  <div class="flex flex-col items-center mt-1">
                    <span class="text-[14px] font-medium text-foreground"
                      >₦{{ pkg.price.toLocaleString() }}</span
                    >
                    <span
                      v-if="pkg.original_price > pkg.price"
                      class="text-[11px] text-muted-foreground line-through"
                    >
                      ₦{{ pkg.original_price.toLocaleString() }}
                    </span>
                  </div>
                </button>
                <button
                  @click="selectCustom"
                  :class="[
                    'px-4 py-4 flex flex-col items-center justify-center rounded-xl border-2 transition-all',
                    isCustom
                      ? 'border-foreground bg-foreground/5 shadow-sm'
                      : 'border-border hover:border-foreground/50',
                  ]"
                >
                  <span class="text-[16px] font-semibold text-foreground"
                    >Custom</span
                  >
                  <span class="text-[13px] text-muted-foreground mt-1"
                    >Other amount</span
                  >
                </button>
              </div>
            </div>
          </div>

          <!-- Package Savings Breakdown -->

          <!-- Info Message -->
          <div class="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
            <div
              class="w-5 h-5 rounded-full bg-foreground flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4V6M6 8H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p class="text-[13px] text-muted-foreground">
              Each learning goal creation requires 10 credits
            </p>
          </div>

          <!-- Purchase Button -->
          <button
            @click="purchaseCredits"
            :disabled="
              purchasing || (isCustom && (calculating || customCredits < 10))
            "
            class="w-full py-4 bg-foreground text-background rounded-full hover:opacity-90 transition-opacity font-medium text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ purchasing ? "Processing..." : "Purchase credits" }}
          </button>
        </div>
      </div>
    </div>
    <!-- Payment Confirmation Overlay -->
    <div
      v-if="paymentStage"
      class="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 transition-all duration-300"
    >
      <div
        class="relative bg-card w-full max-w-[500px] m-auto flex-col flex rounded-2xl shadow-2xl border border-border p-8 animate-in fade-in zoom-in duration-200"
      >
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-xl text-foreground font-bold tracking-tight">
            Complete Payment
          </h2>
          <button
            @click="closePayment"
            class="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-muted"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="flex flex-col items-center space-y-8">
          <div class="text-center space-y-2">
            <p class="text-[15px] text-muted-foreground">
              You are about to purchase
            </p>
            <p class="text-[14px] font-medium text-foreground">
              {{ displayCredits }} Credits
            </p>
            <p class="text-3xl font-extrabold text-foreground mt-2">
              ₦{{ amount?.toLocaleString() }}
            </p>
          </div>

          <div class="flex gap-4 w-full pt-4">
            <button
              @click="closePayment"
              class="flex-1 py-3.5 px-4 bg-muted border border-border text-foreground rounded-full hover:bg-muted/80 transition-all font-medium"
            >
              Cancel
            </button>
            <paystack
              v-if="payment_key && reference"
              :buttonClass="'flex-1 py-3.5 px-4 bg-foreground text-background rounded-full hover:opacity-90 transition-all cursor-pointer text-center font-bold'"
              :buttonText="'Proceed with Payment'"
              :amount="amount * 100"
              :email="email"
              :publicKey="payment_key"
              :reference="reference"
              :onSuccess="processPayment"
              :onCancel="closePayment"
            />
          </div>

          <p class="text-[13px] text-muted-foreground text-center">
            You will be redirected to Paystack to complete your payment
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import Loader from "../ui/Loader.vue";
import paystack from "vue3-paystack";
import { RefreshCw } from "lucide-vue-next";

export default {
  name: "CreditsComponent",
  components: {
    Loader,
    RefreshCw,
    paystack,
  },
  data() {
    return {
      loading: true,
      totalCredits: 0,
      usedCredits: 0,
      totalCreditsLimit: 0,
      creditsUsage: [],
      currentPage: 1,
      itemsPerPage: 10,
      showTopUpDialog: false,
      selectedPackage: null,
      customCredits: "",
      isCustom: false,
      calculating: false,
      calculationResult: null,
      creditPackages: [],
      purchasing: false,
      subscriptionData: null,
      loadingPackages: false,
      paymentStage: false,
      payment_key: null,
      reference: null,
      email: null,
      amount: null,
    };
  },
  computed: {
    usagePercentage() {
      if (this.totalCreditsLimit === 0) return 0;
      return Math.min(
        100,
        Math.round((this.usedCredits / this.totalCreditsLimit) * 100),
      );
    },
    totalPages() {
      return Math.ceil(this.creditsUsage.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(
        this.startIndex + this.itemsPerPage,
        this.creditsUsage.length,
      );
    },
    paginatedCreditsUsage() {
      return this.creditsUsage.slice(this.startIndex, this.endIndex);
    },
    displayAmount() {
      if (this.isCustom) {
        return this.calculationResult?.price
          ? this.calculationResult.price.toLocaleString()
          : "0";
      }
      return this.selectedPackage
        ? this.selectedPackage.price.toLocaleString()
        : "0";
    },
    displayCredits() {
      if (this.isCustom) {
        return this.customCredits || "0";
      }
      return this.selectedPackage?.credits || "0";
    },
  },
  watch: {
    customCredits(newVal) {
      if (this.isCustom && newVal > 0) {
        this.debouncedCalculatePrice();
      } else {
        this.calculationResult = null;
      }
    },
  },
  methods: {
    async fetchCredits() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          "/web/subscriptions/my-subscription",
        );

        if (response.data && response.data.data) {
          const subscription = response.data.data;
          this.totalCredits = subscription.current_credits || 0;
        }
      } catch (error) {
        console.error("Error fetching credits data:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchCreditPackages() {
      try {
        this.loadingPackages = true;
        const response = await apiClient.get(
          "/web/subscriptions/credits/packages",
        );
        if (response.data && response.data.data) {
          this.creditPackages = response.data.data;
          if (this.creditPackages.length > 0) {
            this.selectedPackage = this.creditPackages[0];
          }
        }
      } catch (error) {
        console.error("Error fetching credit packages:", error);
      } finally {
        this.loadingPackages = false;
      }
    },
    async calculatePrice() {
      if (!this.customCredits || this.customCredits < 10) {
        this.calculationResult = null;
        return;
      }
      try {
        this.calculating = true;
        const response = await apiClient.post(
          "/web/subscriptions/credits/calculate",
          {
            credits: parseInt(this.customCredits),
          },
        );
        if (response.data && response.data.data) {
          this.calculationResult = response.data.data;
        }
      } catch (error) {
        console.error("Error calculating price:", error);
      } finally {
        this.calculating = false;
      }
    },
    debouncedCalculatePrice() {
      if (this.calculationTimeout) clearTimeout(this.calculationTimeout);
      this.calculationTimeout = setTimeout(() => {
        this.calculatePrice();
      }, 1000);
    },
    async fetchCreditsUsage() {
      try {
        this.loading = true;
        const response = await apiClient.get("/web/subscriptions/credit-usage");

        if (response.data && response.data.data) {
          const subscriptionData = response.data.data;
          this.creditsUsage = subscriptionData || [];
          this.totalCredits = subscriptionData.current_credits || 0;
          this.totalCreditsLimit = subscriptionData.total_credits || 0;
          this.usedCredits = this.totalCreditsLimit - this.totalCredits;
        }
      } catch (error) {
        console.error("Error fetching credits data:", error);
        this.totalCredits = 0;
        this.totalCreditsLimit = 0;
        this.usedCredits = 0;
      } finally {
        this.loading = false;
      }
    },
    async fetchTransactions() {
      try {
        // Mock data for now as per original implementation
        this.transactions = [
          {
            id: 1,
            purpose: "Learning goal creation",
            credits: 20,
            date: "2024-12-14T10:30:00Z",
          },
          {
            id: 2,
            purpose: "Assessment",
            credits: 20,
            date: "2024-12-14T14:20:00Z",
          },
          {
            id: 3,
            purpose: "Certificate Purchase",
            credits: 20,
            date: "2024-12-14T16:45:00Z",
          },
        ];
      } catch (error) {
        console.error("Error fetching transactions:", error);
        this.transactions = [];
      }
    },
    formatCredits(credits) {
      return credits.toLocaleString();
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    },
    navigateToPricing() {
      this.$router.push({ name: "Pricing" });
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    selectPackage(pkg) {
      this.isCustom = false;
      this.selectedPackage = pkg;
      this.calculationResult = null;
    },
    async selectCustom() {
      this.isCustom = true;
      this.selectedPackage = null;
      this.calculationResult = null;
      this.customCredits = "";
      await this.$nextTick();
      if (this.$refs.customInput) {
        this.$refs.customInput.focus();
      }
    },
    async purchaseCredits() {
      const credits = this.isCustom
        ? this.customCredits
        : this.selectedPackage?.credits;

      if (!credits || credits <= 0) {
        this.$toast.error(
          "Error",
          "Please select a package or enter a valid amount of credits.",
        );
        return;
      }

      try {
        this.purchasing = true;
        const response = await apiClient.post(
          "/web/subscriptions/credits/purchase",
          {
            credits: parseInt(credits),
          },
        );

        if (response.data && response.data.data) {
          const data = response.data.data;
          this.payment_key = data.payment_key;
          this.email = this.$store.state.user?.email || "";
          this.amount = data.amount;
          this.reference = data.reference;
          this.paymentStage = true;
          this.showTopUpDialog = false; // Close the initial modal
        } else {
          throw new Error("Failed to initialize purchase");
        }
      } catch (error) {
        console.error("Error purchasing credits:", error);
        this.$toast.error(
          "Purchase Failed",
          error.response?.data?.message ||
            "Failed to initiate purchase. Please try again.",
        );
      } finally {
        this.purchasing = false;
      }
    },
    processPayment() {
      this.$toast.success("Success", "Credit top-up completed successfully!");
      this.paymentStage = false;
      this.fetchCredits();
      this.fetchCreditsUsage();
      this.fetchTransactions();
    },
    closePayment() {
      this.paymentStage = false;
      this.showTopUpDialog = true; // Go back to top-up dialog if cancelled
    },
  },
  mounted() {
    this.fetchCredits();
    this.fetchTransactions();
    this.fetchCreditsUsage();
    this.fetchCreditPackages();
  },
};
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
